import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch
} from "react-hook-form";
import React, {useEffect, useState} from "react";
import SignUpModel from "./model/SignUpModel";
import {
  EMAIL_MAX_LENGTH,
  EMAIL_REGEX,
  FIRST_NAME_MAX_LENGTH,
  LAST_NAME_MAX_LENGTH,
  MIN_PASSWORD_LENGTH,
  PASSWORD_REGEX,
  USERNAME_MAX_LENGTH,
  USERNAME_REGEX
} from "../NewResearcherConstants";

/**
 * Email tooltip
 */
const emailTooltip = (props: any) => (
    <Tooltip id="emailTooltip" {...props}>
      This must be an email address unique to you. This will be used to send you verification
      messages.
    </Tooltip>
);
/**
 * Username tooltip
 */
const usernameTooltip = (props: any) => (
  <Tooltip id="usernameTooltip" {...props}>
    <div>Usernames must follow this pattern:
      <ul>
        <li>Must not be longer than 64 characters</li>
        <li>Must only contain alphanumeric character or the following special
          characters [',.,_,!,#,^,~,-]
        </li>
        <li>Must not contain @ anywhere</li>
        <li>Must not contain more than one dot (.) in a row</li>
        <li>Must not begin or end in a dot (.)</li>
      </ul>
    </div>
  </Tooltip>
);
/**
 * Password confirmation tooltip
 */
const passwordTooltip = (props: any) => (
    <Tooltip id="passwordTooltip" {...props}>
      Enter password again for confirmation.
    </Tooltip>
);

/**
 * Props for this component
 */
interface SignUpFormProps {
  hasInitialEmail: boolean;
  register: UseFormRegister<SignUpModel>;
  errors: FieldErrors<SignUpModel>;
  watch: UseFormWatch<SignUpModel>;
  setError: UseFormSetError<SignUpModel>;
  setValue: UseFormSetValue<SignUpModel>;
  trigger: UseFormTrigger<SignUpModel>;
}

/**
 * This component is the actual sign up form that is used in the Sign-Up Page
 */
export default function SignUpForm({
                                     hasInitialEmail,
                                     register,
                                     errors,
                                     watch,
                                     setError,
                                     setValue,
                                     trigger
                                   }: SignUpFormProps) {
  // This will be used when showing the warning for the username being the same as email
  const [usernameWarning, setUsernameWarning] = useState<boolean>(false);
  /**
   * This will track if the user has edited the username field to unlink it from the email if they have
   */
  const [usernameLocked, setUsernameLocked] = useState(false);

  // Need to pull these two registrations here so we can correctly make use of the onChange validation
  const emailReg = register("email", {
    required: "Email is required",
    maxLength: {
      value: EMAIL_MAX_LENGTH,
      message: `Email must be at most ${EMAIL_MAX_LENGTH} characters`
    },
    pattern: {
      value: EMAIL_REGEX,
      message: "Please enter a valid email address"
    },
    validate: (v) => {
      // This is not a real validation but is done here to be evaluated after value change
      if (v === usernameValue && !usernameWarning) {
        setUsernameWarning(true);
      }
      if (usernameWarning) {
        setUsernameWarning(false);
      }
      return true;
    }
  });
  const usernameReg = register("username", {
    required: "User Name is required",
    maxLength: {
      value: USERNAME_MAX_LENGTH,
      message: `User Name must be at most ${USERNAME_MAX_LENGTH} characters`
    },
    pattern: {
      value: USERNAME_REGEX,
      message: "Your username does not meet the required format. Hover over the help icon for the username rules."
    },
    validate: (v) => {
      if (v?.trim().length < 1) {
        return "User Name cannot be empty";
      }
      // This is not a real validation but is done here to be evaluated after value change
      if (v === emailValue && !usernameWarning) {
        setUsernameWarning(true);
      }
      if (usernameWarning) {
        setUsernameWarning(false);
      }
      return true;
    }
  });

  /**
   * When we update the email, it needs more behaviour than simply saving the new value. We need to check if the
   * username has already been edited, and if not then we should populate it with the part of the email before the @
   */
  const onChangeToEmailAddress = (event: any) => {
    // Update username if valid
    if (!usernameLocked) {

      // Workout which part of the email we need
      const relevantPart = event.target.value.split("@")[0];

      // Set it to both element and form data
      setValue("username", relevantPart, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  }

  // Needed to check if password isn't equal to username and that the email and username are not the same
  const usernameValue = watch("username");
  const emailValue = watch("email");

  // Watch variables so we can make sure the values match
  const passwordValue = watch("password");
  const confirmPasswordValue = watch("confirmPassword");
  useEffect(() => {
    if (passwordValue.length !== 0 || confirmPasswordValue.length !== 0) {
      trigger(["password", "confirmPassword"])
    }
  }, [passwordValue, confirmPasswordValue, usernameValue, trigger]);

  return <>
    {/*First Name*/
    }
    <Form.Group as={Row} className="mb-2" controlId="formHorizontalFirstName">
      <Form.Label column sm={2}>
        First Name
      </Form.Label>
      <Col sm={10}>
        <Form.Control
            type="text"
            placeholder="First Name"
            {...register("firstName", {
              required: "First Name is required",
              maxLength: {
                value: FIRST_NAME_MAX_LENGTH,
                message: `First Name must be at most ${FIRST_NAME_MAX_LENGTH} characters`
              },
              validate: (v) => (v?.trim().length ? true : "First Name cannot be empty")
            })}
            isInvalid={!!errors.firstName}
            data-testid="first-name"
        />
        <Form.Control.Feedback type="invalid" data-testid="first-name-error">
          {errors.firstName?.message}
        </Form.Control.Feedback>
      </Col>
    </Form.Group>
    {/*Last Name*/
    }
    <Form.Group as={Row} className="mb-4" controlId="formHorizontalLastName">
      <Form.Label column sm={2}>
        Last Name
      </Form.Label>
      <Col sm={10}>
        <Form.Control
            type="text"
            placeholder="Last Name"
            {...register("lastName", {
              required: "Last Name is required",
              maxLength: {
                value: LAST_NAME_MAX_LENGTH,
                message: `Last Name must be at most ${LAST_NAME_MAX_LENGTH} characters`
              },
              validate: (v) => (v?.trim().length ? true : "Last Name cannot be empty")
            })}
            isInvalid={!!errors.lastName}
            data-testid="last-name"
        />
        <Form.Control.Feedback type="invalid" data-testid="last-name-error">
          {errors.lastName?.message}
        </Form.Control.Feedback>
      </Col>
    </Form.Group>
    {/*Email*/
    }
    <Form.Group as={Row} className="mb-2" controlId="formHorizontalEmail">
      <Form.Label column sm={2}>
        Email Address <OverlayTrigger
          placement="top"
          delay={{show: 50, hide: 200}}
          overlay={emailTooltip}
      >
        <i className="bi bi-question-circle-fill"></i>
      </OverlayTrigger>
      </Form.Label>
      <Col sm={10}>
        <Form.Control
            type="email"
            placeholder="Email"
            readOnly={!!hasInitialEmail}
            {...emailReg}
            isInvalid={!!errors.email}
            onChange={(e) => {
              emailReg.onChange(e);
              onChangeToEmailAddress(e);
            }}
            data-testid="email"
        />
        <Form.Control.Feedback type="invalid" data-testid="email-error">
          {errors.email?.message}
        </Form.Control.Feedback>
      </Col>
    </Form.Group>
    {/*User Name*/
    }
    <Form.Group as={Row} className="mb-4" controlId="formHorizontalUserName">
      <Form.Label column sm={2}>
        User Name <OverlayTrigger
        placement="top"
        delay={{show: 50, hide: 200}}
        overlay={usernameTooltip}
      >
        <i className="bi bi-question-circle-fill"></i>
      </OverlayTrigger>
      </Form.Label>
      <Col sm={10}>
        <Form.Control
            type="text"
            placeholder="User Name"
            {...usernameReg}
            isInvalid={!!errors.username}
            onChange={(e) => {
              usernameReg.onChange(e);
              setUsernameLocked(true);
            }}
            data-testid="user-name"
        />
        {
            usernameWarning &&
            <text className={"question-help-text"}>
              If you set your username to be the same as the email you won't be able to change it
              later
            </text>
        }
        <Form.Control.Feedback type="invalid" data-testid="user-name-error">
          {errors.username?.message}
        </Form.Control.Feedback>
      </Col>
    </Form.Group>
    {/*Password*/
    }
    <Form.Group as={Row} className="mb-2" controlId="formHorizontalPassword">
      <Form.Label column sm={2}>
        {/*Need this empty line to align with the text*/}
        <br/>
        Password
      </Form.Label>
      <Col sm={10}>
        <text className={"question-help-text"}>Passwords must be at least 12 characters long with
          some of: uppercase
          letters, lowercase letters, digits, and punctuation.
        </text>
        <Form.Control
            type="password"
            placeholder="Password"
            {...register("password", {
              required: "Password is required",
              minLength: {
                value: MIN_PASSWORD_LENGTH,
                message: `Your password must be at least ${MIN_PASSWORD_LENGTH} characters long.`
              },
              pattern: {
                value: PASSWORD_REGEX,
                message: `Your password must follow the accepted pattern.`
              },
              validate: (v) => {
                if (v?.trim().length < 1) {
                  return "Passwords cannot contain only white spaces";
                }
                if (v === usernameValue) {
                  return "Your password must not be the same as your username";
                }

                // Validating username must be case-insensitive
                const tempPass = v.toUpperCase();
                const tempUsername = usernameValue.toUpperCase();
                if (tempPass.includes(tempUsername)) {
                  return "Your password must not contain your username";
                }
                if (v !== confirmPasswordValue) {
                  setError("confirmPassword", {type: "validate", message: "Passwords must match"});
                  return "Passwords must match";
                }
                return true;
              }
            })}
            isInvalid={!!errors.password}
            data-testid="password"
        />
        <Form.Control.Feedback type="invalid" data-testid="password-error">
          {errors.password?.message}
        </Form.Control.Feedback>
      </Col>
    </Form.Group>
    {/*Confirm Password*/
    }
    <Form.Group as={Row} className="mb-4" controlId="formHorizontalConfirmPassword">
      <Form.Label column sm={2}>
        Confirm Password <OverlayTrigger
          placement="top"
          delay={{show: 50, hide: 200}}
          overlay={passwordTooltip}
      >
        <i className="bi bi-question-circle-fill"></i>
      </OverlayTrigger>
      </Form.Label>
      <Col sm={10}>
        <Form.Control
            type="password"
            placeholder="Confirm Password"
            {...register("confirmPassword", {
              required: "Confirm Password is required",
              validate: (v) => {
                if (v !== passwordValue) {
                  setError("password", {type: "validate", message: "Passwords must match"});
                  return "Passwords must match";
                }
                return true;
              }
            })}
            isInvalid={!!errors.confirmPassword}
            data-testid="password-confirm"
        />
        <Form.Control.Feedback type="invalid" data-testid="password-confirm-error">
          {errors.confirmPassword?.message}
        </Form.Control.Feedback>
      </Col>
    </Form.Group>
  </>;
}